import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { customElement, property, query } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { IconName } from '@thiememeulenhoff/types';
import { BreakpointMedium } from '@thiememeulenhoff/tokens/core';
import { translate } from '@thiememeulenhoff/translations';
import { DrawerComponent } from '../drawer/drawer.component';
import { tagName } from '../../helpers/element.helper';
import { asideStyles } from './side-panel.styles';

@customElement(tagName('side-panel-aside'))
export class SidePanelAsideComponent extends TmElement {
  static override styles = asideStyles;

  @query('aside')
  panel?: HTMLElement;

  @query('a-drawer')
  drawer?: DrawerComponent;

  @property({ type: String })
  icon?: IconName;

  @property({ type: String })
  label?: string;

  @property({ type: Boolean, reflect: true })
  isMobileView = false;

  @property({ attribute: 'collapsable', type: Boolean, reflect: true })
  isCollapsable = false;

  @property({ attribute: 'position', reflect: true })
  position: 'start' | 'end' = 'start';

  @property({ type: Boolean, attribute: 'showPartly' })
  showPartly = false;

  @property({
    type: Boolean,
    reflect: true,
    converter: {
      fromAttribute: (value?: string | null) => value !== 'false',
      toAttribute: (value: boolean) => (value ? '' : null),
    },
  })
  set open(val: boolean) {
    if (!val) {
      this.drawer?.close();
      this.dispatchGlobalEvent({
        eventName: 'close',
        detail: val,
      });
    } else {
      this.drawer?.open();
      this.panel?.focus();
      this.dispatchGlobalEvent({
        eventName: 'open',
        detail: val,
      });
    }

    this.isOpen = val;
    this.dispatchGlobalEvent({
      eventName: 'toggle',
      detail: val,
    });
  }

  get open() {
    return this.isOpen;
  }

  @property({
    attribute: 'is-open',
    reflect: true,
    type: Boolean,
  })
  private isOpen = false;

  @property({ attribute: 'close-button-aria-label' })
  closeButtonAriaLabel = 'common.side-panel.aria-label';

  #iconTemplate() {
    return this.icon ? html`<a-icon name=${this.icon}></a-icon>` : nothing;
  }

  #renderPanel() {
    const labelTemplate =
      this.isCollapsable && !this.isOpen
        ? nothing
        : html`<span class="label">
            ${this.#iconTemplate()}
            <slot name="header"> ${this.label} </slot>
          </span>`;

    return html`<aside part="panel" tabindex="0" data-test="aside">
      <div part="header" class="header">
        ${labelTemplate}
        <a-icon-button
          class="close-button"
          icon-size="default"
          icon="${
            this.isCollapsable && !this.isOpen ? 'chevron-right' : 'close'
          }"
          variant="ghost"
          @click=${this.toggle}
          data-test="close-button"
          aria-label="${translate(this.#closeButtonAriaLabel())}"
        ></a-icon-button>
      </div>
      <div part="main" class="main">
        <slot></slot>
      </div>
    </aside>`;
  }

  #renderMobilePanel() {
    const drawerPlacement = this.position === 'start' ? 'left' : 'right';

    return this.open
      ? html`<a-drawer
          placement="${drawerPlacement}"
          ?show-partly=${this.showPartly}
          keep-alive
          @close=${this.close}
          exportparts="header, main"
        >
          <span slot="title" class="label">
            ${this.#iconTemplate()}
            <slot name="header"> ${this.label} </slot>
          </span>
          <slot></slot>
        </a-drawer>`
      : nothing;
  }

  #closeButtonAriaLabel() {
    if (this.isCollapsable) {
      return this.isOpen
        ? `${this.closeButtonAriaLabel}.collapse`
        : `${this.closeButtonAriaLabel}.expand`;
    }

    return this.isOpen
      ? `${this.closeButtonAriaLabel}.close`
      : `${this.closeButtonAriaLabel}.open`;
  }

  override render() {
    return this.isMobileView ? this.#renderMobilePanel() : this.#renderPanel();
  }

  #isMobile = () => window.innerWidth < parseInt(BreakpointMedium, 10);

  #handleResize = () => {
    const newIsMobile = this.#isMobile();

    if (newIsMobile !== this.isMobileView) {
      this.isMobileView = newIsMobile;
    }
  };

  protected override onCreate = () => {
    this.isMobileView = this.#isMobile();

    window.addEventListener('resize', this.#handleResize);
  };

  protected override onDestroy = () => {
    window.removeEventListener('resize', this.#handleResize);
  };

  close() {
    this.open = false;
  }

  toggle() {
    this.open = !this.open;
  }

  closeDrawer() {
    this.drawer?.close();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-side-panel-aside': SidePanelAsideComponent;
  }
}
